export type AwaitedSearchParams = Record<string, string | string[] | undefined>;

/**
 * Normalize Next.js' awaited `searchParams` shape into a `URLSearchParams` instance.
 *
 * Next gives pages a `Record<string, string | string[] | undefined>` once
 * awaited. Passing that directly to `new URLSearchParams(...)` is unsafe:
 *
 *   - TypeScript rejects it (the constructor expects `Record<string, string>`).
 *   - At runtime, arrays get joined with commas (`?a=x&a=y` becomes `a=x%2Cy`)
 *     and `undefined` values are coerced to the literal string `"undefined"`.
 *
 * This helper appends each entry explicitly so multi-valued keys round-trip
 * correctly via `.getAll()` and missing keys stay missing.
 */
export function toURLSearchParams(searchParams: AwaitedSearchParams | undefined): URLSearchParams {
	const params = new URLSearchParams();
	if (!searchParams) return params;
	for (const [key, value] of Object.entries(searchParams)) {
		if (value === undefined) continue;
		if (Array.isArray(value)) {
			value.forEach(v => params.append(key, v));
		} else {
			params.append(key, value);
		}
	}
	return params;
}
